import React from 'react';
import { Bar } from 'react-chartjs-2';
import type { ChartOptions } from 'chart.js';
import { PelletIcon } from '../Global/Icons';
import { FadeAccordion, FadeAccordionItem } from '../Global/Transitions';
import { Accordion, Col, Row } from 'react-bootstrap';
import { Chart, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import Phase from '../../Models/Phase';
import ExportButton from './ReportExport';
import Project from '../../Models/Project';
import RatingProps from '../../Utilities/RatingProps';
import { useTheme } from '../Global/ThemeContextType';

interface ReportSummaryChartProps {
  phases: Phase[];
  project: Project;
}

Chart.register(Legend, Tooltip, BarElement, LinearScale, CategoryScale);
Chart.defaults.elements.bar.borderRadius = 0;

const ReportSummaryChart: React.FC<ReportSummaryChartProps> = ({ phases, project }) => {
  const { darkMode } = useTheme();
  const ratings = [1, 2, 3, 4, 5];
  const counts: Record<string, Record<number, number>> = {};
  // Mirrors the category legend in the report itself (_UsingThisReport.cshtml). Any
  // attack_class__type not listed here is dropped from the chart entirely.
  const categories = ['Vulnerability', 'Weakness', 'Information Leakage', 'Note'];

  categories.forEach(type => {
    counts[type] = {};
    ratings.forEach(rating => (counts[type][rating] = 0));
  });

  // Aggregate counts
  phases?.forEach(phase => {
    phase.findings?.forEach(finding => {
      const { attack_class__type, rating } = finding;
      if (categories.includes(attack_class__type) && ratings.includes(rating)) {
        counts[attack_class__type][rating]++;
      }
    });
  });

  // Filter categories/ratings with data
  const filteredCategories = categories.filter(category =>
    ratings.some(rating => counts[category][rating] > 0)
  );

  const filteredRatings = ratings.filter(rating =>
    filteredCategories.some(category => counts[category][rating] > 0)
  );

  // pick hex colors (with alpha) for light/dark
const isDark = !!darkMode;

const GRID_COLOR = isDark ? '#FFFFFF26' : '#00000014'; // ~15% white vs ~8% black

const options: ChartOptions<'bar'> = {
  elements: { bar: { borderRadius: 0 } },
  datasets: { bar: { borderRadius: 0 } },
  maxBarThickness: 50,
  plugins: { legend: { display: false } },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 13, family: 'Satoshi, sans-serif' } }
    },
    y: {
      grid: { color: GRID_COLOR },   // 👈 hex color for grid lines
      ticks: { stepSize: 1, font: { size: 13, family: 'Satoshi, sans-serif' } }
    }
  }
} as any;


  const chartdata = {
    labels: filteredCategories,
    datasets: filteredRatings.map(rating => ({
      // keep per-dataset 0 to override any theme defaults
      borderRadius: 0,
      label: RatingProps[rating].name,
      backgroundColor: RatingProps[rating].bg,
      hoverBackgroundColor: RatingProps[rating].hover,
      data: filteredCategories.map(type => counts[type][rating])
    }))
  };

  return (
    <FadeAccordion defaultActiveKey="default">
      <FadeAccordionItem eventKey="default" className="position-relative">
        <Row className="border-bottom m-0 p-2">
          <Col><h3 className="pt-1">Findings by Category and Rating</h3></Col>
          <Col className="text-end pe-2"><ExportButton data={project} /></Col>
        </Row>
        <Accordion.Body>
          {ratings.map(rating => (
            <p key={rating} className="d-inline-block me-5">
              <PelletIcon className={RatingProps[rating].color} />
              {RatingProps[rating].name}
            </p>
          ))}
          <Bar data={chartdata} options={options} />
        </Accordion.Body>
      </FadeAccordionItem>
    </FadeAccordion>
  );
};

export default ReportSummaryChart;
